/**
 * ACCOUNTS AND GROUPS — the client half (§ F201). The rules are the server's
 * (`src/routes/account.ts`, `src/routes/groups.ts`, migration 037).
 */

import { authHeaders, type StoredPlayer } from "./player";
import type { AvatarValue } from "./roster";

export class AccountApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(
  path: string,
  opts: { method?: string; body?: unknown; player?: StoredPlayer | null } = {}
): Promise<T> {
  const headers: Record<string, string> = opts.player
    ? { Authorization: `Bearer ${opts.player.token}` }
    : authHeaders();
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: AbortSignal.timeout(15_000),
    });
  } catch {
    throw new AccountApiError("Network error — try again", 0);
  }
  let payload: { success?: boolean; error?: string; data?: T } | null = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok || !payload?.success) {
    throw new AccountApiError(payload?.error || "Something went wrong — try again", res.status);
  }
  return payload.data as T;
}

// ── sign up / sign in ───────────────────────────────────────────────────────

export function requestRegister(input: {
  name: string;
  email: string;
  phone: string;
  password: string;
  accept_terms: boolean;
  turnstile: string | null;
}): Promise<{ message: string }> {
  return call("/api/account/register", { method: "POST", body: input, player: null });
}

/** § F206 — email + password; answers with a session straight away. */
export async function loginWithPassword(input: {
  email: string;
  password: string;
  turnstile: string | null;
}): Promise<StoredPlayer> {
  const data = await call<{ player_id: string; name: string; token: string }>("/api/account/login", {
    method: "POST",
    body: input,
    player: null,
  });
  return { id: data.player_id, name: data.name, token: data.token };
}

export function requestPasswordReset(input: { email: string; turnstile: string | null }): Promise<{ message: string }> {
  return call("/api/account/forgot", { method: "POST", body: input, player: null });
}

export async function resetLinkIsLive(token: string): Promise<boolean> {
  const data = await call<{ live: boolean }>(`/api/account/reset/${encodeURIComponent(token)}`, { player: null });
  return data.live;
}

export function finishPasswordReset(token: string, password: string): Promise<void> {
  return call("/api/account/reset", { method: "POST", body: { token, password }, player: null });
}

export function changePassword(current: string, password: string): Promise<void> {
  return call("/api/account/password", { method: "POST", body: { current, password } });
}

export const PASSWORD_MIN = 8;

export async function verifyLoginCode(email: string, code: string): Promise<StoredPlayer & { created: boolean }> {
  const data = await call<{ player_id: string; name: string; token: string; created: boolean }>(
    "/api/account/verify",
    { method: "POST", body: { email, code }, player: null }
  );
  return { id: data.player_id, name: data.name, token: data.token, created: data.created };
}

export interface Me {
  player_id: string;
  name: string;
  email: string | null;
  phone: string | null;
}

export function fetchMe(player?: StoredPlayer): Promise<Me> {
  return call("/api/account/me", { player });
}

export function updatePhone(phone: string): Promise<{ phone: string | null }> {
  return call("/api/account/me", { method: "PATCH", body: { phone } });
}

export function deleteAccount(): Promise<void> {
  return call("/api/account/me", { method: "DELETE", body: { confirm: "DELETE" } });
}

// ── groups ──────────────────────────────────────────────────────────────────

export interface GroupMember {
  id: string;
  name: string;
  display_name: string;
  role: "owner" | "member";
  avatar: AvatarValue | null;
  color: string | null;
  ink: string | null;
}

export interface Group {
  id: string;
  name: string;
  role: "owner" | "member";
  /** § F208 — invites this group has sent that are not answered yet. */
  invited: { id: string; player_id: string; display_name: string }[];
  members: GroupMember[];
}

export async function fetchGroups(): Promise<Group[]> {
  return (await call<{ groups: Group[] }>("/api/groups")).groups;
}

export async function createGroup(name: string): Promise<string> {
  return (await call<{ group_id: string }>("/api/groups", { method: "POST", body: { name } })).group_id;
}

export function leaveGroup(groupId: string): Promise<void> {
  return call(`/api/groups/${groupId}/leave`, { method: "POST" });
}

export function renameGroup(groupId: string, name: string): Promise<void> {
  return call(`/api/groups/${groupId}/rename`, { method: "POST", body: { name } });
}

export function removeMember(groupId: string, playerId: string): Promise<void> {
  return call(`/api/groups/${groupId}/remove`, { method: "POST", body: { player_id: playerId } });
}

export function transferOwnership(groupId: string, playerId: string): Promise<void> {
  return call(`/api/groups/${groupId}/transfer`, { method: "POST", body: { player_id: playerId } });
}

// ── § F208 — in-app invites (no code, no link, no email) ───────────────────

export interface FoundPlayer {
  id: string;
  display_name: string;
  avatar: AvatarValue | null;
  color: string | null;
  ink: string | null;
}

/** Name contains `q` (2+ characters), or email is exactly `q`. */
export async function searchPlayers(q: string): Promise<FoundPlayer[]> {
  return (await call<{ players: FoundPlayer[] }>(`/api/players/search?q=${encodeURIComponent(q)}`)).players;
}

export function invitePlayer(groupId: string, playerId: string): Promise<void> {
  return call(`/api/groups/${groupId}/invite`, { method: "POST", body: { player_id: playerId } });
}

export interface GroupInvite {
  id: string;
  group_id: string;
  group_name: string;
  invited_by: string | null;
  member_count: number;
}

export async function fetchInvites(): Promise<GroupInvite[]> {
  return (await call<{ invites: GroupInvite[] }>("/api/groups/invites")).invites;
}

export function answerInvite(inviteId: string, accept: boolean): Promise<void> {
  return call(`/api/groups/invites/${inviteId}/${accept ? "accept" : "decline"}`, { method: "POST" });
}

export function cancelInvite(inviteId: string): Promise<void> {
  return call(`/api/groups/invites/${inviteId}/cancel`, { method: "POST" });
}

/** Anything that changes my invites or groups says so; the header badge and the prompt listen. */
export const INVITES_CHANGED = "gtw-invites-changed";
export function invitesChanged(): void {
  window.dispatchEvent(new Event(INVITES_CHANGED));
}

// ── the active group (per device) ───────────────────────────────────────────
// Rankings are for ONE group at a time when you are in several; which one is a
// per-device convenience, so it is localStorage and nothing depends on it.

const ACTIVE_GROUP_KEY = "gtw_active_group";

export function loadActiveGroup(): string | null {
  try {
    return localStorage.getItem(ACTIVE_GROUP_KEY);
  } catch {
    return null;
  }
}

export function saveActiveGroup(groupId: string | null): void {
  try {
    if (groupId) localStorage.setItem(ACTIVE_GROUP_KEY, groupId);
    else localStorage.removeItem(ACTIVE_GROUP_KEY);
  } catch {
    /* private mode */
  }
}
